import { useMemo } from "react";
import type { SubjectItem } from "../model/types";
import {
  getVisibleSubjectTree,
  normalizeSubjectSearch,
} from "../model/subjectSingleModel";

interface UseSubjectTreeViewModelArgs {
  subjectSearchQuery: string;
  rootSubjects: SubjectItem[];
  buckets: Record<string, SubjectItem[]>;
  expandedIds: Set<number>;
  pickerMode: "tree" | "list";
  searchCollapsedIds: Set<number>;
  focusedSubjectId: number | null;
  preferredPickerSubject: SubjectItem | null;
}

export const useSubjectTreeViewModel = ({
  subjectSearchQuery,
  rootSubjects,
  buckets,
  expandedIds,
  pickerMode,
  searchCollapsedIds,
  focusedSubjectId,
  preferredPickerSubject,
}: UseSubjectTreeViewModelArgs) => {
  const normalizedSubjectSearchQuery = useMemo(
    () => normalizeSubjectSearch(subjectSearchQuery),
    [subjectSearchQuery],
  );
  const isSubjectSearchActive = normalizedSubjectSearchQuery.length > 0;
  const subjectTreeView = useMemo(
    () =>
      getVisibleSubjectTree(
        rootSubjects,
        buckets,
        expandedIds,
        pickerMode,
        normalizedSubjectSearchQuery,
        searchCollapsedIds,
      ),
    [
      buckets,
      expandedIds,
      normalizedSubjectSearchQuery,
      pickerMode,
      rootSubjects,
      searchCollapsedIds,
    ],
  );
  const visibleSubjectRows = subjectTreeView.rows;
  const effectiveSubjectExpandedIds = subjectTreeView.effectiveExpandedIds;
  const subjectSearchMatchCount = subjectTreeView.matchCount;
  const subjectSearchMatchingIds = subjectTreeView.matchingIds;
  const firstSubjectSearchMatchId = subjectTreeView.firstMatchId;
  const focusedSubject = useMemo(
    () =>
      visibleSubjectRows.find((row) => row.subject.id === focusedSubjectId)
        ?.subject ||
      preferredPickerSubject ||
      visibleSubjectRows[0]?.subject ||
      null,
    [focusedSubjectId, preferredPickerSubject, visibleSubjectRows],
  );

  return {
    normalizedSubjectSearchQuery,
    isSubjectSearchActive,
    subjectTreeView,
    visibleSubjectRows,
    effectiveSubjectExpandedIds,
    subjectSearchMatchCount,
    subjectSearchMatchingIds,
    firstSubjectSearchMatchId,
    focusedSubject,
  };
};
